import React, {Component} from 'react';
import {Button, MenuItem} from '@material-ui/core';
import Form, {FormStatus} from 'components/forms/Form';
import SelectField from 'components/forms/SelectField';
import RadioGroupField from 'components/forms/RadioGroupField';
import Note from '../Note';
import {ValidationGroup, Validators} from 'util/validation';
import {updateBookingInsurance} from 'services/bookings';
import {handleError} from 'util/errors';
import {Booking, BookingInsuranceOptions} from 'services/entities/booking';
import ProgressSpinner from 'components/ProgressSpinner';
import {SuccessIcon} from 'components/Icon';
import './index.scss';

interface BookingInsuranceFormState {
  status: FormStatus;
}

interface BookingInsuranceFormProps {
  booking: Booking;
}

interface BookingInsuranceFormModel {
  type: string|null;
  foodDrink: 'yes'|'no'|null;
  hasInsurance: 'yes'|'no'|'not-sure'|null;
}

export default class BookingInsuranceForm extends Component<BookingInsuranceFormProps, BookingInsuranceFormState> {
  state = {
    status: FormStatus.None
  };

  private readonly validation: ValidationGroup;
  private readonly model:BookingInsuranceFormModel;

  constructor(props:BookingInsuranceFormProps) {
    super(props);
    this.model = {
      type: null,
      foodDrink: null,
      hasInsurance: null
    };
    this.validation = new ValidationGroup(this.model);
    this.validation.addField('type', {'required': Validators.required});
    this.validation.addField('foodDrink', {'required': Validators.required});
    this.validation.addField('hasInsurance', {'required': Validators.required});
  }

  onFieldInput = () => {
    this.setState({ status: FormStatus.None });
  };

  onSubmit = () => {
    this.setState({ status: FormStatus.Submitting });
    updateBookingInsurance(this.props.booking.code, this.model as BookingInsuranceOptions).then(
      () => this.setState({status: FormStatus.Success}),
      () => {
        this.setState({status: FormStatus.Failure});
        handleError('Failed to save insurance options');
      }
    )
  };

  renderSuccessMessage() {
    return <div className="booking-insurance-form success">
      <h3><SuccessIcon/> Your request has been submitted.</h3>
      <p>A member of our team will be in touch to discuss insurance options.</p>
    </div>;
  }

  renderForm() {
    const enabled = this.state.status === FormStatus.None || this.state.status === FormStatus.Failure;
    return <Form className="booking-insurance-form" validationGroup={this.validation} onSubmit={this.onSubmit}>
      <div className="field">
        <label>What do you have planned?</label>
        <SelectField
          disabled={!enabled}
          name="type"
          validationGroup={this.validation}
          model={this.model}
          validationMessages={{'required': 'What you have planned is required'}}
          onChange={this.onFieldInput}>
          <MenuItem value="retail">Retail space</MenuItem>
        </SelectField>
      </div>
      <div className="field">
        <label>Will you be serving food and/or drink?</label>
        <SelectField
          disabled={!enabled}
          name="foodDrink"
          validationGroup={this.validation}
          model={this.model}
          validationMessages={{'required': 'Serving food and/or drink is required'}}
          onChange={this.onFieldInput}>
          <MenuItem value="yes">Yes</MenuItem>
          <MenuItem value="no">No</MenuItem>
        </SelectField>
      </div>
      <div className="field insurance">
        <h4>Insurance</h4>
        <label>Do you have all appropriate insurances/coverage/licences to sell and serve food and/or drinks?</label>
        <RadioGroupField
          name="hasInsurance"
          validationGroup={this.validation}
          model={this.model}
          validationMessages={{'required': 'Insurance status is required'}}
          options={[
            {value: 'yes', label: 'Yes'},
            {value: 'no', label: 'No'},
            {value: 'not-sure', label: 'I am not sure'}
          ]}
          onChange={this.onFieldInput}/>
      </div>
      <Note>After submitting this form a member of our team will be in touch to discuss insurance options.</Note>
      <div className="actions">
        <Button type="submit" disabled={!enabled}>
          {this.state.status === FormStatus.Submitting && <ProgressSpinner />}
          Submit
        </Button>
      </div>
    </Form>;
  }

  render() {
    if(this.state.status === FormStatus.Success) {
      return this.renderSuccessMessage();
    } else {
      return this.renderForm();
    }
  }
}